import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton, Alert } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import AppNavigation from './AppNavigation';
import PostCard from './PostCard';
import { Post, postService } from '../services/postService';
import { authService } from '../services/authService';

const PostDetail: React.FC = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const currentUser = authService.getUser();
  const userId = currentUser?.userId ?? null;

  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await postService.getPost(postId);
      setPost(result);
    } catch {
      setError('Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = () => {
    navigate('/feed');
  };

  return (
    <Box>
      <AppNavigation />
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 4, maxWidth: 640, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
          <IconButton onClick={() => navigate(-1)} aria-label="Back">
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Post</Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && !post && (
          <Alert severity="info">
            This post isn't available. It may have been deleted, or you may not have permission to view it.
          </Alert>
        )}

        {!loading && !error && post && (
          <PostCard post={post} currentUserId={userId} onDelete={handleDelete} />
        )}
      </Box>
    </Box>
  );
};

export default PostDetail;
